import { Exclude } from 'class-transformer';
import {
  IsBoolean,
  IsNumber,
  IsOptional,
  IsString,
  IsUUID,
  Max,
  Min,
  ValidateNested,
} from 'class-validator';
import { BaseCreateDto, BaseDto, BaseUpdateDto } from '../base.dto';
import { NamedEntityDto } from '../clinical-details/named.dto';
import { DiagnosisDto } from '../diagnoses';
import { ModelViewerLocation } from '../model-viewer-location.dto';
import { TrainingConditionImageDto } from './training-condition-image.dto';
import { TrainingPatientDto } from './training-patient.dto';

export interface TrainingConditionDto extends BaseDto {
  legacyIdentifier?: string;
  patientAgeYears?: number;
  dermoscopyFollowTimeMonths?: number;
  preciseLocation?: ModelViewerLocation;
  histopathologyConducted: boolean;
  teledermatologyConducted: boolean;
  sentinelNodeBiopsyConducted: boolean;
  recurrence?: boolean;
  recurrenceTimeMonths?: number;
  totalTimeOfFollowUpMonths?: number;
  mcq: boolean;
  status: NamedEntityDto;
  skinType?: NamedEntityDto;
  patient: TrainingPatientDto;
  location?: NamedEntityDto;
  // Actual diagnosis of training condition. Revised diagnosis takes precedence
  diagnosis?: DiagnosisDto;
  // Actual secondary diagnosis of training condition. Revised secondary diagnosis takes precedence
  secondaryDiagnosis?: DiagnosisDto;
  source?: NamedEntityDto;
  dermoscopicImage?: TrainingConditionImageDto;
  clinicalImage?: TrainingConditionImageDto;
  images?: TrainingConditionImageDto[];
  anonymized?: boolean;
}

/**
 * DTO used for updating the focal point of a training condition image.
 * Used in UpdateTrainingConditionDto.
 */
export class UpdateTrainingConditionFocalPointDto {
  @IsUUID()
  imageId: string;

  @IsNumber()
  @Min(0)
  @Max(1)
  x?: number;

  @IsNumber()
  @Min(0)
  @Max(1)
  y?: number;
}

export class CreateTrainingConditionDto extends BaseCreateDto {
  @IsUUID()
  statusId: string;

  @IsUUID()
  patientId: string;

  @IsBoolean()
  histopathologyConducted: boolean;

  @IsBoolean()
  teledermatologyConducted: boolean;

  @IsBoolean()
  sentinelNodeBiopsyConducted: boolean;

  @IsBoolean()
  mcq: boolean;
}

/**
 * DTO for updating an existing training condition.
 */
export class UpdateTrainingConditionDto
  extends BaseUpdateDto
  implements Partial<TrainingConditionDto>
{
  @IsOptional()
  @IsString()
  legacyIdentifier?: string;

  @IsOptional()
  @IsNumber()
  @Min(0)
  patientAgeYears?: number;

  @IsOptional()
  @IsNumber()
  @Min(0)
  dermoscopyFollowTimeMonths?: number;

  @IsOptional()
  @ValidateNested()
  preciseLocation?: ModelViewerLocation;

  @IsOptional()
  @IsBoolean()
  histopathologyConducted?: boolean;

  @IsOptional()
  @IsBoolean()
  sentinelNodeBiopsyConducted?: boolean;

  @IsOptional()
  @IsBoolean()
  recurrence?: boolean;

  @IsOptional()
  @IsNumber()
  @Min(0)
  recurrenceTimeMonths?: number;

  @IsOptional()
  @IsNumber()
  @Min(0)
  totalTimeOfFollowUpMonths?: number;

  @IsOptional()
  @IsBoolean()
  mcq?: boolean;

  @IsOptional()
  @IsUUID()
  statusId?: string;

  @Exclude()
  status?: never;

  @Exclude()
  patient?: never;

  // If set, updates the existing patients gender
  @IsOptional()
  @IsUUID()
  genderId?: string;

  @IsOptional()
  @IsUUID()
  locationId?: string;

  @Exclude()
  location?: never;

  @IsOptional()
  @IsUUID()
  revisedDiagnosisId?: string;

  @Exclude()
  diagnosis?: never;

  @IsOptional()
  @IsUUID()
  revisedSecondaryDiagnosisId?: string;

  @Exclude()
  secondaryDiagnosis?: never;

  @IsOptional()
  @IsUUID()
  skinTypeId?: string;

  @Exclude()
  skinType?: never;

  @Exclude()
  source?: never;

  @Exclude()
  dermoscopicImage?: never;

  @Exclude()
  clinicalImage?: never;

  @IsOptional()
  @IsBoolean()
  anonymized?: boolean;

  @IsOptional()
  @ValidateNested()
  updatedFocalPoint?: UpdateTrainingConditionFocalPointDto;
}
